import * as B from '@blueprintjs/core';
import {useVirtualizer} from '@tanstack/react-virtual';
import React from 'react';
import {TableInstance} from 'react-table';

import {ApiShareLink} from 'app/modules/Remote/ShareLink';

import cs from '../PropertyOverview.module.scss';
import {TableHeader} from '../PropertyTable';
import {VirtualizedTableRow} from '../VirtualizedTableRow';
import {MAX_SELECTED_SHARE_LINKS} from './shareLinksTableState';

export type ShareLinksStatus = 'loading' | 'error' | 'done';

// selection, property, type, created by, created, link
export const GRID_TEMPLATE_COLUMNS =
  '5.6rem minmax(14rem, 1.5fr) minmax(14rem, 1.5fr) minmax(10rem, 1fr) minmax(9rem, 0.8fr) minmax(7rem, 0.5fr)';

const PortfolioShareLinksTable: React.FunctionComponent<{
  table: TableInstance<ApiShareLink>;
  status: ShareLinksStatus;
  hasShareLinks: boolean;
  allowSelection: boolean;
  onDeleteSelected: () => void;
}> = ({table, status, hasShareLinks, allowSelection, onDeleteSelected}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: table.rows.length,
    estimateSize: () => 56,
    getScrollElement: () => tableContainerRef.current,
    getItemKey: (index) => table.rows[index].id,
  });

  const openShareLink = React.useCallback(
    (link: ApiShareLink) => window.open(link.url, '_blank', 'noopener,noreferrer'),
    []
  );

  const selectedCount = table.selectedFlatRows.length;

  if (status === 'loading') {
    return (
      <div className={cs.tableContainer}>
        <B.Spinner className={cs.tableLoadingSpinner} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={cs.tableContainer}>
        <B.NonIdealState
          icon="error"
          description="There was an error loading share links. Please refresh and try again."
        />
      </div>
    );
  }

  return (
    <div className={cs.tableContainer} ref={tableContainerRef}>
      <table>
        <thead>
          <TableHeader table={table} gridTemplateColumns={GRID_TEMPLATE_COLUMNS} />
          {selectedCount > 0 && (
            <tr
              className={cs.selectedRowActionsContainer}
              style={{gridTemplateColumns: GRID_TEMPLATE_COLUMNS}}
            >
              <th style={{padding: 0, width: '100vw'}}>
                <SelectedShareLinksActions
                  count={selectedCount}
                  onDelete={onDeleteSelected}
                  onClear={() => table.toggleAllRowsSelected(false)}
                />
              </th>
            </tr>
          )}
        </thead>
        <tbody style={{height: `${rowVirtualizer.getTotalSize()}px`}}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = table.rows[virtualRow.index];
            table.prepareRow(row);
            return (
              <VirtualizedTableRow
                key={row.id}
                row={row}
                offset={virtualRow.start}
                gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                onOpen={openShareLink}
                isSelectable={(link) => allowSelection && link.canDelete}
              />
            );
          })}
        </tbody>
      </table>
      {table.rows.length === 0 && (
        <div className={cs.emptyListMessage}>
          {hasShareLinks ? (
            <>
              No share links match these filters.
              <br />
              <a
                onClick={(ev) => {
                  ev.preventDefault();
                  table.setAllFilters([]);
                }}
              >
                Show all share links
              </a>
            </>
          ) : (
            'No share links for this portfolio yet.'
          )}
        </div>
      )}
    </div>
  );
};

const SelectedShareLinksActions: React.FunctionComponent<{
  count: number;
  onDelete: () => void;
  onClear: () => void;
}> = ({count, onDelete, onClear}) => {
  const isOverLimit = count > MAX_SELECTED_SHARE_LINKS;

  return (
    <div className={cs.selectedRowActions}>
      <div className={cs.selectedRowSummary}>
        <strong>{count === 1 ? '1 share link selected' : `${count} share links selected`}</strong> –{' '}
        <a
          onClick={(ev) => {
            ev.preventDefault();
            onClear();
          }}
        >
          Clear selection
        </a>
      </div>
      <div className={cs.selectedRowButtons}>
        <B.Tooltip
          content={`Delete up to ${MAX_SELECTED_SHARE_LINKS} share links at a time`}
          disabled={!isOverLimit}
        >
          <B.AnchorButton icon="trash" text="Delete" disabled={isOverLimit} onClick={onDelete} />
        </B.Tooltip>
      </div>
    </div>
  );
};

export default PortfolioShareLinksTable;
